import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCostCodes, createCostCode, updateCostCode, deleteCostCode } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2, Tags } from 'lucide-react';

const COST_CODE_REGEX = /^[A-Za-z0-9-]{1,8}$/;

export default function CostCodesPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [costCodes, setCostCodes] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({ code: '', name: '', active: true, type: 'main' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchCostCodes();
    }, []);

    const fetchCostCodes = async () => {
        try {
            const res = await getCostCodes();
            setCostCodes(res.data);
        } catch (error) {
            toast.error(t('cost_codes.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setEditing(null);
        setFormData({ code: '', name: '', active: true, type: 'main' });
        setDialogOpen(true);
    };

    const openEditDialog = (cc) => {
        setEditing(cc);
        setFormData({ code: cc.code, name: cc.name, active: cc.active, type: cc.type || 'main' });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        const trimmedCode = formData.code.trim().toUpperCase();
        const trimmedName = formData.name.trim();

        if (!trimmedCode) {
            toast.error(t('cost_codes.code_required'));
            return;
        }
        if (!COST_CODE_REGEX.test(trimmedCode)) {
            toast.error(t('cost_codes.code_invalid'));
            return;
        }
        if (!trimmedName) {
            toast.error(t('cost_codes.name_required'));
            return;
        }

        setSaving(true);
        try {
            const payload = { code: trimmedCode, name: trimmedName, active: formData.active, type: formData.type };
            if (editing) {
                await updateCostCode(editing.id, payload);
                toast.success(t('cost_codes.updated'));
            } else {
                await createCostCode(payload);
                toast.success(t('cost_codes.created'));
            }
            setDialogOpen(false);
            fetchCostCodes();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('cost_codes.failed_save'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (cc) => {
        if (!window.confirm(t('cost_codes.delete_confirm', { code: cc.code, name: cc.name }))) return;
        try {
            await deleteCostCode(cc.id);
            toast.success(t('cost_codes.deleted'));
            fetchCostCodes();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('cost_codes.failed_delete'));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="cost-codes-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('cost_codes.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('cost_codes.subtitle')}</p>
                </div>
                <Button
                    className="uppercase tracking-wider"
                    onClick={openCreateDialog}
                    data-testid="add-cost-code-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('cost_codes.add')}
                </Button>
            </div>

            {/* List */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    {costCodes.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <Tags className="w-12 h-12 mx-auto mb-4 opacity-30" />
                            <p>{t('cost_codes.empty')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>{t('cost_codes.col_code')}</th>
                                        <th>{t('cost_codes.col_name')}</th>
                                        <th>{t('cost_codes.type_label')}</th>
                                        <th>{t('cost_codes.col_status')}</th>
                                        <th className="text-right">{t('cost_codes.col_actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {costCodes.map((cc) => (
                                        <tr key={cc.id}>
                                            <td className="font-mono font-semibold">{cc.code}</td>
                                            <td>{cc.name}</td>
                                            <td>
                                                <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${
                                                    (cc.type || 'main') === 'main'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-muted text-muted-foreground'
                                                }`}>
                                                    {(cc.type || 'main') === 'main'
                                                        ? t('cost_codes.type_main')
                                                        : t('cost_codes.type_sub')}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${
                                                    cc.active
                                                        ? 'bg-foreground text-background'
                                                        : 'bg-muted text-muted-foreground'
                                                }`}>
                                                    {cc.active ? t('common.active') : t('common.inactive')}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEditDialog(cc)}
                                                    data-testid={`edit-cost-code-${cc.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(cc)}
                                                    className="text-destructive hover:text-destructive"
                                                    data-testid={`delete-cost-code-${cc.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editing ? t('cost_codes.edit_title') : t('cost_codes.new_title')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('cost_codes.label_code')}</Label>
                            <Input
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                placeholder={t('cost_codes.placeholder_code')}
                                maxLength={8}
                                data-testid="cost-code-code-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">{t('cost_codes.code_hint')}</p>
                        </div>

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('cost_codes.label_name')}</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('cost_codes.placeholder_name')}
                                data-testid="cost-code-name-input"
                            />
                        </div>

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('cost_codes.type_label')}</Label>
                            <Select
                                value={formData.type || 'main'}
                                onValueChange={(v) => setFormData({ ...formData, type: v })}
                            >
                                <SelectTrigger data-testid="cost-code-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="main">{t('cost_codes.type_main')}</SelectItem>
                                    <SelectItem value="sub">{t('cost_codes.type_sub')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center justify-between">
                            <Label className="label-uppercase">{t('cost_codes.label_active')}</Label>
                            <Switch
                                checked={formData.active}
                                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                                data-testid="cost-code-active-switch"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-cost-code-btn">
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
